import {
  ApiListParams,
  ApiListResponse,
  ApiResponse,
  BookingCount,
  BookingDetail,
  BookingListParams,
  BookingShipment,
  BookingShipmentActivityFilter,
  BookingShipmentActivity,
  BookingShipmentFilter,
  BookingAssignDriverPayload,
  BookingShipmentGrouped,
  Booking,
  BookingActivity,
  ShipmentActivityPayload,
  DriverAssignment,
  DriverAssignmentFilter,
  BookingShipmentGroupedV2,
  BookingDraft,
  BookingDraftCount,
  BookingDraftInfo,
} from "../types";
import { api, getVersionedApi } from "../libs";

export const getBookingData: (
  params?: BookingListParams
) => Promise<ApiListResponse<Booking>> = async (params) => {
  try {
    const response = await api.get("/bookings", {
      params: {
        page: params?.page,
        limit: params?.limit,
        ...params?.filters,
        status:
          params?.filters?.status === "all"
            ? undefined
            : params?.filters?.status,
      },
    });
    return response.data;
  } catch (error) {
    console.log("Error fetching booking data:", JSON.stringify(error));
    throw error;
  }
};

export const getBookingCount: () => Promise<
  ApiResponse<BookingCount>
> = async () => {
  try {
    const response = await api.get("/bookings/status-counts");
    return response.data;
  } catch (error) {
    console.error("Error fetching booking count:", error);
    throw error;
  }
};

export const getBookingShipment: (
  params: ApiListParams<BookingShipmentFilter>
) => Promise<ApiListResponse<BookingShipment>> = async (params) => {
  try {
    const { booking_ulid, ...filters } = params.filters || {};
    const response = await api.get(`/bookings/${booking_ulid}/shipments`, {
      params: {
        page: params.page,
        limit: params.limit,
        ...filters,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching booking shipment:", error);
    throw error;
  }
};

export const getBookingById: (
  id: string
) => Promise<ApiResponse<BookingDetail>> = async (id) => {
  try {
    const response = await api.get(`/bookings/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching booking by id:", error);
    throw error;
  }
};

export const getBookingShipmentActivities: (
  id: string,
  params: ApiListParams<BookingShipmentActivityFilter>
) => Promise<ApiListResponse<BookingShipmentActivity>> = async (id, params) => {
  try {
    const response = await api.get(`/bookings/${id}/shipment-activities`, {
      params: {
        page: params.page,
        limit: params.limit,
        ...params.filters,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching booking shipment activities:", error);
    throw error;
  }
};

export const getBookingActivities: (
  id: string,
  params: ApiListParams<any>
) => Promise<ApiListResponse<BookingActivity>> = async (id, params) => {
  try {
    const response = await api.get(`/bookings/${id}/activities`, {
      params: {
        page: params.page,
        limit: params.limit,
        ...params.filters,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching booking activities:", error);
    throw error;
  }
};

export const postBookingDriverAssignment: (
  id: string,
  payload: BookingAssignDriverPayload
) => Promise<ApiResponse<any>> = async (id, payload) => {
  try {
    const response = await api.post(`/bookings/${id}/assignment`, payload);
    return response.data;
  } catch (error) {
    console.error("Error booking driver assignment:", error);
    throw error;
  }
};

export const getBookingShipmentGrouped: (
  id: string,
  params: ApiListParams<any>
) => Promise<ApiListResponse<BookingShipmentGrouped>> = async (id, params) => {
  try {
    const response = await api.get(`/bookings/${id}/shipments-grouped`, {
      params: {
        page: params.page,
        limit: params.limit,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching booking shipment grouped:", error);
    throw error;
  }
};

export const postCreateShipmentActivty: (
  id: string,
  payload: ShipmentActivityPayload
) => Promise<ApiResponse<any>> = async (id, payload) => {
  try {
    const finalPayload = {
      ...payload,
      additional_data: Array.isArray(payload.conditions)
        ? {
            conditions: payload.conditions,
          }
        : undefined,
    };
    delete finalPayload.conditions;
    const response = await api.post(
      `/bookings/${id}/shipment-activities`,
      finalPayload
    );
    return response.data;
  } catch (error) {
    console.error("Error create shipment activity:", error);
    throw error;
  }
};

export const deleteShipmentActivity: (
  bookingId: string,
  ids: string[]
) => Promise<ApiResponse<any>> = async (bookingId, ids) => {
  try {
    const response = await api.delete(
      `/bookings/${bookingId}/shipment-activities`,
      {
        data: {
          activity_ulids: ids,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error delete shipment activity:", error);
    throw error;
  }
};

export const getDriverAssignments: (
  params: ApiListParams<DriverAssignmentFilter>
) => Promise<ApiListResponse<DriverAssignment>> = async (params) => {
  try {
    const response = await api.get(`/drivers/assignments`, {
      params: {
        page: params.page,
        limit: params.limit,
        ...params.filters,
        status:
          params?.filters?.status === "all"
            ? undefined
            : params?.filters?.status,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching driver assignments:", error);
    throw error;
  }
};

export const getDriverAssignmentsById: (
  id: string
) => Promise<ApiResponse<DriverAssignment>> = async (id) => {
  try {
    const response = await api.get(`/drivers/assignments/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching driver assignments by id:", error);
    throw error;
  }
};

export const getBookingShipmentGroupedV2: (
  id: string,
  params: ApiListParams<any>
) => Promise<ApiListResponse<BookingShipmentGroupedV2>> = async (
  id,
  params
) => {
  try {
    const apiV2 = getVersionedApi("v2");
    const response = await apiV2.get(`/bookings/${id}/shipments-grouped`, {
      params: {
        page: params.page,
        limit: params.limit,
      },
    });
    return response.data;
  } catch (error) {
    console.log(JSON.stringify(error));
    console.error("Error fetching booking shipment grouped:", error);
    throw error;
  }
};

export const getBookingDrafts: (
  params?: ApiListParams<any>
) => Promise<ApiListResponse<BookingDraft>> = async (params) => {
  try {
    let reqPlantDeliveryDateFilter = {};
    if (params?.filters?.req_plant_delivery_date) {
      reqPlantDeliveryDateFilter = {
        req_plant_delivery_date_start:
          params.filters?.req_plant_delivery_date[0],
        req_plant_delivery_date_end: params.filters?.req_plant_delivery_date[1],
      };
      delete params.filters.req_plant_delivery_date;
    }
    const response = await api.get(`/booking-drafts`, {
      params: params
        ? {
            page: params.page,
            limit: params.limit,
            ...params.filters,
            ...reqPlantDeliveryDateFilter,
          }
        : undefined,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching booking drafts:", error);
    throw error;
  }
};

export const getBookingDraftCount: () => Promise<
  ApiResponse<BookingDraftCount>
> = async () => {
  try {
    const response = await api.get("/booking-drafts/status-counts");
    return response.data;
  } catch (error) {
    console.error("Error fetching booking count:", error);
    throw error;
  }
};

export const getBookingDraftInfo: (
  id: string
) => Promise<ApiResponse<BookingDraftInfo>> = async (id) => {
  try {
    const response = await api.get(`/booking-drafts/${id}/info`);
    return response.data;
  } catch (error) {
    console.error("Error fetching booking draft info:", error);
    throw error;
  }
};

export const postBookingDraftConfirm: (
  id: string,
  payload: any
) => Promise<ApiResponse<any>> = async (id, payload) => {
  try {
    const response = await api.post(`/booking-drafts/${id}/confirm`, payload);
    return response.data;
  } catch (error) {
    console.error("Error confirming booking draft:", error);
    throw error;
  }
};

